import type { TFunction } from 'i18next';
import type { EntitlementFeatureKey } from '@/types/package';

/** Brand names of the shop plans (not translated). Unknown slugs fall back to the generic wording. */
export const PLAN_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  plan_creator: 'Brand Creator',
  plan_elevator: 'Brand Elevator',
  plan_accelerator: 'Brand Accelerator',
  plan_maison: 'Brands Maison',
};

export const getPlanDisplayName = (slug: string | null | undefined): string | null =>
  slug ? PLAN_DISPLAY_NAMES[slug] ?? null : null;

/** Where the CTA goes: the plan's details in the Shop, or the Shop itself when the plan is unknown. */
export const getShopPlanPath = (slug: string | null | undefined): string =>
  slug && PLAN_DISPLAY_NAMES[slug] ? `/shop?details=${encodeURIComponent(slug)}` : '/shop';

/** Brand Portfolio / release registry keys that have their own locked-card copy */
type PortfolioFeatureKey =
  | 'portfolio_saved_views'
  | 'portfolio_compare'
  | 'portfolio_timeline'
  | 'portfolio_workload'
  | 'portfolio_inbox'
  | 'portfolio_status_update'
  | 'portfolio_digest'
  | 'portfolio_plan_batch'
  | 'portfolio_sentiment'
  | 'portfolio_highlights'
  | 'portfolio_repurposing'
  | 'portfolio_brand_lead'
  | 'portfolio_client_relationship'
  | 'release_carte_blanche';

export interface FeatureCopy {
  title: string;
  benefit: string;
}

/**
 * Title + one value line per gated feature. Literal t() calls on purpose (i18n-audit extracts them).
 */
export const getFeatureCopy = (t: TFunction, feature: string): FeatureCopy => {
  const copy: Partial<Record<EntitlementFeatureKey | PortfolioFeatureKey, FeatureCopy>> = {
    analytics_hub: {
      title: t('gating_feature_analytics_hub_title'),
      benefit: t('gating_feature_analytics_hub_benefit'),
    },
    analytics_compare: {
      title: t('gating_feature_analytics_compare_title'),
      benefit: t('gating_feature_analytics_compare_benefit'),
    },
    best_times: {
      title: t('gating_feature_best_times_title'),
      benefit: t('gating_feature_best_times_benefit'),
    },
    lara_insights: {
      title: t('gating_feature_lara_insights_title'),
      benefit: t('gating_feature_lara_insights_benefit'),
    },
    trend_suggestions: {
      title: t('gating_feature_trend_suggestions_title'),
      benefit: t('gating_feature_trend_suggestions_benefit'),
    },
    brand_world: {
      title: t('gating_feature_brand_world_title'),
      benefit: t('gating_feature_brand_world_benefit'),
    },
    auto_translate: {
      title: t('gating_feature_auto_translate_title'),
      benefit: t('gating_feature_auto_translate_benefit'),
    },
    weekly_goals: {
      title: t('gating_feature_weekly_goals_title'),
      benefit: t('gating_feature_weekly_goals_benefit'),
    },
    festivities: {
      title: t('gating_feature_festivities_title'),
      benefit: t('gating_feature_festivities_benefit'),
    },
    team_roles: {
      title: t('gating_feature_team_roles_title'),
      benefit: t('gating_feature_team_roles_benefit'),
    },
    wordpress: {
      title: t('gating_feature_wordpress_title'),
      benefit: t('gating_feature_wordpress_benefit'),
    },
    brand_check: {
      title: t('gating_feature_brand_check_title'),
      benefit: t('gating_feature_brand_check_benefit'),
    },
    canva: {
      title: t('gating_feature_canva_title'),
      benefit: t('gating_feature_canva_benefit'),
    },
    gdrive: {
      title: t('gating_feature_gdrive_title'),
      benefit: t('gating_feature_gdrive_benefit'),
    },
    portfolio_saved_views: {
      title: t('gating_feature_portfolio_saved_views_title'),
      benefit: t('gating_feature_portfolio_saved_views_benefit'),
    },
    portfolio_compare: {
      title: t('gating_feature_portfolio_compare_title'),
      benefit: t('gating_feature_portfolio_compare_benefit'),
    },
    portfolio_timeline: {
      title: t('gating_feature_portfolio_timeline_title'),
      benefit: t('gating_feature_portfolio_timeline_benefit'),
    },
    portfolio_workload: {
      title: t('gating_feature_portfolio_workload_title'),
      benefit: t('gating_feature_portfolio_workload_benefit'),
    },
    portfolio_inbox: {
      title: t('gating_feature_portfolio_inbox_title'),
      benefit: t('gating_feature_portfolio_inbox_benefit'),
    },
    portfolio_status_update: {
      title: t('gating_feature_portfolio_status_update_title'),
      benefit: t('gating_feature_portfolio_status_update_benefit'),
    },
    portfolio_digest: {
      title: t('gating_feature_portfolio_digest_title'),
      benefit: t('gating_feature_portfolio_digest_benefit'),
    },
    portfolio_plan_batch: {
      title: t('gating_feature_portfolio_plan_batch_title'),
      benefit: t('gating_feature_portfolio_plan_batch_benefit'),
    },
    portfolio_sentiment: {
      title: t('gating_feature_portfolio_sentiment_title'),
      benefit: t('gating_feature_portfolio_sentiment_benefit'),
    },
    portfolio_highlights: {
      title: t('gating_feature_portfolio_highlights_title'),
      benefit: t('gating_feature_portfolio_highlights_benefit'),
    },
    portfolio_repurposing: {
      title: t('gating_feature_portfolio_repurposing_title'),
      benefit: t('gating_feature_portfolio_repurposing_benefit'),
    },
    portfolio_brand_lead: {
      title: t('gating_feature_portfolio_brand_lead_title'),
      benefit: t('gating_feature_portfolio_brand_lead_benefit'),
    },
    release_carte_blanche: {
      title: t('gating_feature_release_carte_blanche_title'),
      benefit: t('gating_feature_release_carte_blanche_benefit'),
    },
    portfolio_client_relationship: {
      title: t('gating_feature_portfolio_client_relationship_title'),
      benefit: t('gating_feature_portfolio_client_relationship_benefit'),
    },
  };
  return (
    copy[feature as EntitlementFeatureKey | PortfolioFeatureKey] ?? {
      title: t('gating_feature_generic_title'),
      benefit: t('gating_feature_generic_benefit'),
    }
  );
};

/** Display names of the gateable social platforms (brand names, not translated). */
export const GATED_PLATFORM_LABELS: Readonly<Record<string, string>> = {
  LINKEDIN: 'LinkedIn',
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  TIKTOK: 'TikTok',
  PINTEREST: 'Pinterest',
  THREADS: 'Threads',
  GOOGLE_MY_BUSINESS: 'Google Business',
  BLUESKY: 'Bluesky',
};

export const getPlatformLabel = (platform: string): string => GATED_PLATFORM_LABELS[platform] ?? platform;

/**
 * Any count that selects the plural form in every locale (fr treats 0 as singular, pl needs the genitive "many").
 * Used where the unit stands without a number ("doesn't include {{unit}}").
 */
export const QUOTA_UNIT_PLURAL_COUNT = 5;

/**
 * Name of a quota unit ("blog post" / "blog posts"), inflected for `count` via i18next plural suffixes.
 * Literal t() calls on purpose (i18n-audit extracts them). Unknown keys fall back to a generic unit.
 */
export const getQuotaUnit = (t: TFunction, quotaKey: string, count: number = QUOTA_UNIT_PLURAL_COUNT): string => {
  switch (quotaKey) {
    case 'blog_posts':
      return t('gating_quota_unit_blog_posts', { count });
    case 'social_posts_published':
      return t('gating_quota_unit_social_posts_published', { count });
    case 'ai_image_generations':
      return t('gating_quota_unit_ai_image_generations', { count });
    case 'brand_check_runs':
      return t('gating_quota_unit_brand_check_runs', { count });
    case 'blog_to_social_generations':
      return t('gating_quota_unit_blog_to_social_generations', { count });
    case 'trend_analyses':
      return t('gating_quota_unit_trend_analyses', { count });
    case 'translations':
      return t('gating_quota_unit_translations', { count });
    case 'ai_insights':
      return t('gating_quota_unit_ai_insights', { count });
    // kept for re-enable (registry entry disabled, see package-quotas.ts)
    case 'ai_text_generations':
      return t('gating_quota_unit_ai_text_generations', { count });
    case 'brand_profiles':
      return t('gating_quota_unit_brand_profiles', { count });
    default:
      return t('gating_quota_unit_generic', { count });
  }
};

export interface QuotaCopyInput {
  quotaKey: string;
  used: number | null;
  limit: number | null;
  resetsAt: string | null;
  /** true = a fixed per-brand count limit (not a monthly quota) */
  fixedLimit?: boolean;
}

/** Title + body of the quota / plan-limit upsell card. Counts are inflected ("1 blog post", "0" gets its own sentence). */
export const getQuotaCardCopy = (
  t: TFunction,
  quota: QuotaCopyInput,
  formatDate: (iso: string) => string
): FeatureCopy => {
  const { quotaKey, used, limit, resetsAt, fixedLimit } = quota;
  if (limit === 0) {
    const unit = getQuotaUnit(t, quotaKey);
    return fixedLimit
      ? { title: t('gating_limit_title'), benefit: t('gating_limit_none', { unit }) }
      : { title: t('gating_quota_title'), benefit: t('gating_quota_none', { unit }) };
  }
  if (fixedLimit && limit !== null) {
    return {
      title: t('gating_limit_title'),
      benefit: t('gating_limit_body', { limit, unit: getQuotaUnit(t, quotaKey, limit) }),
    };
  }
  const title = t('gating_quota_title');
  if (used !== null && limit !== null) {
    const unit = getQuotaUnit(t, quotaKey, limit);
    return {
      title,
      benefit: resetsAt
        ? t('gating_quota_used_resets', { used, limit, unit, date: formatDate(resetsAt) })
        : t('gating_quota_used', { used, limit, unit }),
    };
  }
  return { title, benefit: t('gating_quota_reached_generic', { unit: getQuotaUnit(t, quotaKey) }) };
};
